import { useEffect, useRef, useState } from "react";
import { DISCOVERY_AXES, DISCOVERY_SEARCH_MAX_LENGTH, DISCOVERY_SEVERITIES, TEXT_AXIS, isSeverityRange, discoveryRange, relationshipTerms } from "./discoveryQueries.js";

const PARAM = "discovery";
const MAX_URL_STATE = 16000;
const HISTORY_LIMIT = 30;
const groupAxes = new Set(DISCOVERY_AXES.map((a) => a.id));
const axes = new Set([...groupAxes, TEXT_AXIS.id]);
const text = (value, limit = 1000) => typeof value === "string" && value.length <= limit;
const initialView = () => ({ axis: "reason", findingAxis: "reason", unit: "sessions", order: "most", rankSeverities: [], layout: "visual", search: "", results: "findings" });
export const initialOverviewSettings = () => ({ axis: "department", destination: "severity", minimum: "medium", selection: { group: null, destination: null } });
export const initialDiscoveryNavigation = () => ({ v: 2, scope: { filters: [], relationship: null }, days: 30, range: discoveryRange(30), compare: false, view: initialView(), inspector: null, overview: initialOverviewSettings() });

function constraints(value) {
    if (!Array.isArray(value) || value.length > axes.size || new Set(value.map((f) => f.axis)).size !== value.length) throw new Error("Invalid constraints");
    return value.map((f) => {
        if (!axes.has(f.axis) || (f.value !== null && !(f.axis === "topic" ? Number.isSafeInteger(f.value) && f.value >= -2147483648 && f.value <= 2147483647 : text(f.value) || (f.axis === "severity" && isSeverityRange(f.value))))) throw new Error("Invalid constraint");
        if (f.axis === TEXT_AXIS.id && (!text(f.value, DISCOVERY_SEARCH_MAX_LENGTH) || !f.value.trim())) throw new Error("Invalid finding text search");
        if (f.axis === "topic" && (!text(f.generationId, 128) || !f.generationId)) throw new Error("Missing topic analysis");
        if (f.minimum !== undefined && (f.minimum !== true || f.axis !== "severity" || !DISCOVERY_SEVERITIES.includes(f.value))) throw new Error("Invalid minimum severity");
        if (f.value === "other:" && (!["department", "jobTitle"].includes(f.axis) || !Array.isArray(f.exclude) || f.exclude.length > 8 || f.exclude.some((v) => !text(v) || !v.startsWith("value:")))) throw new Error("Invalid organization remainder");
        return { axis: f.axis, value: f.value, ...(f.value === "other:" ? { exclude: f.exclude } : {}), ...(f.minimum ? { minimum: true } : {}), ...(f.axis === "topic" ? { generationId: f.generationId, ...(text(f.label) ? { label: f.label } : {}) } : {}) };
    });
}
function relationship(value) {
    if (!value) return null;
    return { terms: relationshipTerms(value).map(({ axis, value: term }) => {
        if (!text(term)) throw new Error("Invalid overlap value");
        return { axis, value: term };
    }), filters: constraints(value.filters), expanded: value.expanded === true };
}
export function discoveryMemberReference(member) {
    if (!member) return null;
    const out = {};
    for (const key of ["finding_id", "trace_id", "report_id"]) {
        if (!text(member[key], 128) || !member[key]) throw new Error("Incomplete finding reference");
        out[key] = member[key];
    }
    if (!Number.isFinite(Date.parse(member.completed_at))) throw new Error("Missing review time");
    out.completed_at = new Date(member.completed_at).toISOString();
    for (const key of ["endpoint_id", "reason", "severity"]) out[key] = text(member[key], 128) ? member[key] : null;
    return out;
}
function overviewSettings(value = initialOverviewSettings()) {
    if (!["department", "jobTitle"].includes(value.axis) || !["severity", "reason", "intent", "sensitivity"].includes(value.destination)
        || !["all", ...DISCOVERY_SEVERITIES].includes(value.minimum) || !value.selection
        || [value.selection.group, value.selection.destination].some((key) => key !== null && !text(key))) throw new Error("Invalid organization selection");
    return { axis: value.axis, destination: value.destination, minimum: value.minimum, selection: { group: value.selection.group, destination: value.selection.destination } };
}
export function normalizeDiscoveryNavigation(input) {
    if (![1, 2].includes(input?.v) || ![7, 30, 90].includes(input.days)) throw new Error("Invalid saved exploration");
    const start = Date.parse(input.range?.absolute?.start), end = Date.parse(input.range?.absolute?.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || end - start > 90 * 86400000) throw new Error("Invalid review period");
    const v = input.view;
    const scope = input.v === 1 ? v : input.scope;
    if (!scope) throw new Error("Missing finding scope");
    if (!v || !groupAxes.has(v.axis) || !groupAxes.has(v.findingAxis) || !["endpoints", "sessions", "findings"].includes(v.unit)
        || !["most", "least"].includes(v.order) || !["visual", "table"].includes(v.layout) || !text(v.search, 160)
        || !Array.isArray(v.rankSeverities) || v.rankSeverities.length > 4 || v.rankSeverities.some((s) => !DISCOVERY_SEVERITIES.includes(s))) throw new Error("Invalid exploration view");
    const inspector = input.inspector ? { selected: discoveryMemberReference(input.inspector.selected),
        filters: constraints(input.inspector.filters ?? scope.filters), relationship: relationship(input.inspector.relationship) } : null;
    return { v: 2, scope: { filters: constraints(scope.filters), relationship: relationship(scope.relationship) }, days: input.days, range: { absolute: { start: new Date(start).toISOString(), end: new Date(end).toISOString() } }, compare: input.compare === true, overview: overviewSettings(input.overview),
        view: { axis: v.axis, findingAxis: v.findingAxis, unit: v.axis === "endpoint" && v.unit === "endpoints" ? "findings" : v.unit,
            order: v.order, layout: v.layout, search: v.search, rankSeverities: [...new Set(v.rankSeverities)],
            results: ["findings", "patterns", "groups"].includes(v.results) ? v.results : "findings",
            preview: discoveryMemberReference(v.preview) }, inspector };
}
export function decodeDiscoveryNavigation(raw) {
    if (!raw) return null;
    if (raw.length > MAX_URL_STATE) throw new Error("Saved exploration is too large");
    return normalizeDiscoveryNavigation(JSON.parse(raw));
}
function checkAccess(entry, allowedAxes) {
    const references = [entry.view.axis, entry.view.findingAxis, ...entry.scope.filters.map((f) => f.axis),
        ...(entry.scope.relationship?.filters ?? []).map((f) => f.axis), ...(entry.scope.relationship?.terms ?? []).map((t) => t.axis),
        ...(entry.inspector?.filters ?? []).map((f) => f.axis), ...(entry.inspector?.relationship?.filters ?? []).map((f) => f.axis),
        ...(entry.inspector?.relationship?.terms ?? []).map((t) => t.axis)];
    if (references.some((axis) => !allowedAxes.includes(axis))) throw new Error("Unavailable exploration attribute");
    return entry;
}
function readLocation(allowedAxes) {
    try { return { entry: checkAccess(decodeDiscoveryNavigation(new URLSearchParams(window.location.search).get(PARAM)) ?? initialDiscoveryNavigation(), allowedAxes), error: null }; }
    catch { return { entry: initialDiscoveryNavigation(), error: "This saved exploration could not be restored. Showing all visible findings." }; }
}
function writeLocation(entry, trail, contextKey, push) {
    const raw = JSON.stringify(entry);
    if (raw.length > MAX_URL_STATE) throw new Error("This selection is too large to save as an exploration link. Remove a constraint and try again.");
    const url = new URL(window.location.href);
    url.searchParams.set(PARAM, raw);
    const state = { ...window.history.state, discoveryTrail: { contextKey, entries: trail } };
    if (push && typeof state.idx === "number") state.idx++;
    window.history[push ? "pushState" : "replaceState"](state, "", url);
}

function readTrail(entry, contextKey, allowedAxes) {
    const saved = window.history.state?.discoveryTrail;
    if (saved?.contextKey === contextKey && Array.isArray(saved.entries) && saved.entries.length > 0 && saved.entries.length <= HISTORY_LIMIT) {
        try {
            const trail = saved.entries.map((item) => checkAccess(normalizeDiscoveryNavigation(item), allowedAxes));
            if (JSON.stringify(trail.at(-1)) === JSON.stringify(entry)) return trail;
        } catch { /* Invalid history must not restore an inaccessible selection. */ }
    }
    return [entry];
}

// Browser history stores bounded navigation metadata only. Tenant evidence is
// always read again through the currently authorized API surface.
export function useDiscoveryNavigation(contextKey, { restore = true, allowedAxes = [...axes] } = {}) {
    const [state, setState] = useState(() => {
        const loaded = restore ? readLocation(allowedAxes) : { entry: initialDiscoveryNavigation(), error: null };
        return { ...loaded, trail: restore ? readTrail(loaded.entry, contextKey, allowedAxes) : [loaded.entry], undoScopes: [] };
    });
    const current = useRef(state);
    const publish = (next) => { current.current = next; setState(next); };
    useEffect(() => {
        const pop = () => {
            const loaded = readLocation(allowedAxes);
            const trail = readTrail(loaded.entry, contextKey, allowedAxes);
            if (JSON.stringify(loaded.entry) === JSON.stringify(current.current.entry) && JSON.stringify(trail) === JSON.stringify(current.current.trail.map((entry) => normalizeDiscoveryNavigation(entry)))) return;
            const next = { ...loaded, trail, undoScopes: [] };
            current.current = next; setState(next);
        };
        window.addEventListener("popstate", pop);
        try { writeLocation(current.current.entry, current.current.trail, contextKey, false); }
        catch (error) { setState((s) => ({ ...s, error: error.message })); }
        return () => window.removeEventListener("popstate", pop);
    }, [contextKey]);
    function update(patch, { push = false, undo = false } = {}) {
        const before = current.current;
        try {
            const entry = checkAccess(normalizeDiscoveryNavigation({ ...before.entry, ...patch }), allowedAxes);
            const scopeChanged = JSON.stringify(entry.scope) !== JSON.stringify(before.entry.scope);
            const undoScopes = undo ? before.undoScopes.slice(0, -1) : scopeChanged ? [...before.undoScopes, before.entry.scope].slice(-HISTORY_LIMIT) : before.undoScopes;
            const trail = push ? [...before.trail, entry].slice(-HISTORY_LIMIT) : [...before.trail.slice(0, -1), entry];
            writeLocation(entry, trail, contextKey, push);
            publish({ entry, trail, undoScopes, error: null });
        } catch (error) { publish({ ...before, error: error.message }); }
    }
    return { ...state, update, undoScope: () => {
        const scope = current.current.undoScopes.at(-1);
        if (scope) update({ scope, view: { ...current.current.entry.view, preview: null }, inspector: null }, { push: true, undo: true });
    }, back: () => { if (current.current.trail.length > 1) window.history.back(); } };
}
